import React from 'react'
import { isNil, equals, isEmpty } from 'ramda'
import { useMakeDispatchCallback } from 'hooks'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import PropTypes from 'prop-types'
import { DrawerDivider, Button } from 'components/patterns'
import EdgeImg from 'components/feature/EdgeImg'
import HysteresisImg from 'components/feature/HysteresisImg'
import InputSwitchList from 'components/feature/NavigatorPanes/editors/base/InputSwitchList'

import {
  COMPLEX,
  TIMER,
  CARRY_LOGIC_SET,
  CARRY_TIMER,
  SELF_REFERENCE,
  DIGITAL_INPUT,
  ANALOG_INPUT,
  CAN_INPUT,
  SUBTYPES_WITH_HYSTERESIS,
  QUERY_TYPES_WITH_EVALUATION,
  SIXTEEN_BIT_OUTPUT,
  FEATURE_MODULE_NAMES_WITH_HARNESSES,
} from 'utils/constants'
import { opMap, OPERATORS } from 'components/utils'
import strings from 'utils/strings'
import {
  edgeLabel,
  configurableInputValueLabel,
} from 'components/feature/utils'
import { signalTranslationRangeSymbolLabel } from 'i18n/displayConstants/signals'
import { translationValue } from 'utils/translations'
import { isPinInput } from 'utils/types/features'
import { timerValue } from 'utils/timers'
import { insertIf } from 'utils/types'
import { INTERNAL } from 'constants/featureTypes'
import ReadOnlyParameterValue from 'components/feature/NavigatorPanes/editors/base/ReadOnlyParameterValue'
import BitGrid from 'components/feature/NavigatorPanes/editors/base/BitGrid'
import CanOutputSection from 'components/feature/NavigatorPanes/OutputNodePane/OutputNodeEditor/CanOutputSection'
import { InputConnectorFinder } from 'components/feature/ConnectorFinderMenu'
import {
  StyledImg,
  StyledInputLabel,
  StyledEvaluation,
  ReadOnlyContainer,
} from './ReadOnly.style'

const labelText = ({
  subType,
  carryType,
  selectedInputDescription,
  operator,
  value,
  valueMin,
  valueMax,
  ignoreCarry,
  units,
  translations,
  translationType,
  isSixteenBitCanOutput,
  isScalingQuery,
}) => {
  if (!selectedInputDescription) {
    return strings.undefined_condition
  }

  const rungTypeMassaged = () => {
    if (isSixteenBitCanOutput) return SIXTEEN_BIT_OUTPUT
    if (!ignoreCarry) {
      if (carryType === CARRY_TIMER) return TIMER
      return subType
    }
    if (carryType === CARRY_LOGIC_SET || carryType === SELF_REFERENCE)
      return COMPLEX
    if (carryType === CARRY_TIMER) return TIMER

    return subType
  }

  const valueWithUnits = (val) =>
    `${translationValue(val, translationType, translations)}${
      signalTranslationRangeSymbolLabel(units) || ''
    }`

  const scalingQueryText = `${valueWithUnits(valueMin)} ${
    opMap[OPERATORS.LESS_THAN_OR_EQUAL]
  } ${selectedInputDescription} ${
    opMap[OPERATORS.LESS_THAN_OR_EQUAL]
  } ${valueWithUnits(valueMax)}`

  const labelType = rungTypeMassaged()
  const labelTypeEq = equals(labelType)

  if (labelTypeEq(TIMER)) {
    return `${selectedInputDescription} ${opMap[operator]} ${timerValue(value)}`
  }

  if (
    labelTypeEq(SIXTEEN_BIT_OUTPUT) ||
    labelTypeEq(COMPLEX) ||
    labelTypeEq(SELF_REFERENCE)
  ) {
    return selectedInputDescription
  }

  return isScalingQuery
    ? scalingQueryText
    : `${selectedInputDescription} ${opMap[operator]} ${valueWithUnits(value)}`
}

// TODO: rename file, not an editor
const ReadOnly = ({
  inputUri,
  result,
  hysteresis,
  edge,
  numberOfBits,
  startBit,
  withBitTable,
  inputConfig,
  operator,
  carryType,
  selectedInputDescription,
  value,
  valueMin,
  valueMax,
  ignoreCarry,
  units,
  isSingleCondition,
  translations,
  translationType,
  isSixteenBitCanOutput,
  subType,
  pinLocation,
  switchNumber,
  isScalingQuery,
  featureModuleName,
  floatingCircuit,
  featureType,
  switchDescription,
  currentSignalSwitches,
  canId,
  interfacePins = [],
  customDescription,
}) => {
  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)
  const subTypeEq = equals(subType)

  // don't show for '=' && '=/=' operators
  const showHysteresis =
    !isSingleCondition &&
    !isNil(hysteresis) &&
    operator !== 2 &&
    operator !== 3 &&
    SUBTYPES_WITH_HYSTERESIS.has(subType)

  const getCircuitText = () => {
    if (!inputUri) return strings.no_input_selected
    if (inputUri && !floatingCircuit) return strings.no_input_available

    return floatingCircuit
  }
  const hasSwitches = !isEmpty(currentSignalSwitches)
  const values = [
    ...insertIf(subTypeEq(ANALOG_INPUT), {
      label: isScalingQuery
        ? strings.scaling_measure_label
        : strings.measure_label,
      value: configurableInputValueLabel(inputConfig),
    }),
    ...insertIf(subTypeEq(DIGITAL_INPUT), {
      label: strings.polarity,
      value: configurableInputValueLabel(inputConfig),
    }),
    ...insertIf(showHysteresis, {
      label: strings.hysteresis,
      value: (
        <>
          {hysteresis}{' '}
          <StyledImg>
            <HysteresisImg />
          </StyledImg>
        </>
      ),
    }),
    ...insertIf(edge, {
      label: strings.edge_detection,
      value: (
        <>
          {edgeLabel(edge)}{' '}
          <StyledImg>
            <EdgeImg edge={edge} />
          </StyledImg>
        </>
      ),
    }),
    ...insertIf(isPinInput(subType), {
      label: strings.pin_location(true),
      value: pinLocation,
    }),
    ...insertIf(
      (subTypeEq(ANALOG_INPUT) || subTypeEq(DIGITAL_INPUT)) &&
        FEATURE_MODULE_NAMES_WITH_HARNESSES.includes(featureModuleName) &&
        featureType === INTERNAL,
      {
        label: strings.circuit,
        value: getCircuitText(),
      }
    ),
  ]

  const InputValue = () => {
    if (QUERY_TYPES_WITH_EVALUATION.has(carryType)) {
      return (
        <>
          <StyledInputLabel text={strings.input_from_label} weight="bold" />
          <Button
            onClick={() => {
              setSelectedIdCallback(inputUri)
            }}
            link
          >
            {selectedInputDescription}
          </Button>
          <StyledEvaluation value={result} readonly />
          <DrawerDivider />
        </>
      )
    }

    const showConnectorFinder =
      subType === DIGITAL_INPUT && inputUri && !floatingCircuit

    return (
      <ReadOnlyContainer>
        <ReadOnlyParameterValue
          value={labelText({
            carryType,
            subType,
            selectedInputDescription,
            operator,
            value,
            valueMin,
            valueMax,
            ignoreCarry,
            units,
            translations,
            translationType,
            isSixteenBitCanOutput,
            isScalingQuery,
          })}
          label={isScalingQuery ? strings.scaling_input : strings.input}
        />
        {subType === ANALOG_INPUT && (
          <ReadOnlyParameterValue
            value={customDescription || selectedInputDescription}
            label={strings.custom_ai_pin_name}
          />
        )}
        {subType === DIGITAL_INPUT && (
          <ReadOnlyParameterValue
            value={customDescription || selectedInputDescription}
            label={strings.custom_di_pin_name}
          />
        )}
        {showConnectorFinder && (
          <InputConnectorFinder
            interfacePins={interfacePins}
            inputUri={inputUri}
          />
        )}
        {hasSwitches && (
          <InputSwitchList
            currentInputOrOutputSwitches={currentSignalSwitches}
            marginTop={'10px'}
          />
        )}
        {subType === CAN_INPUT && (
          <ReadOnlyParameterValue
            value={customDescription || selectedInputDescription}
            label={strings.custom_can_input_name}
          />
        )}
        {customDescription &&
          subType !== ANALOG_INPUT &&
          subType !== DIGITAL_INPUT &&
          subType !== CAN_INPUT && (
            <ReadOnlyParameterValue
              value={customDescription}
              label={strings.tem_custom_switch_name}
            />
          )}
        {withBitTable && (
          <CanOutputSection
            mappedOutputId={canId}
            numberOfBits={numberOfBits}
            showCanId={false}
            className="output-container"
          />
        )}
      </ReadOnlyContainer>
    )
  }

  return (
    <>
      {!isScalingQuery && <DrawerDivider />}
      <InputValue />

      {withBitTable && (
        <>
          <BitGrid
            groupSize={numberOfBits}
            type="input"
            disabledBits={[]}
            value={startBit}
            selectionHandler={() => {}}
            readOnly
          />
          {!isSingleCondition && <DrawerDivider />}
        </>
      )}

      {values.map(({ label, value: val, noColon }) => (
        <ReadOnlyParameterValue
          key={label}
          value={val}
          label={label}
          noColon={noColon}
        />
      ))}
    </>
  )
}

export default ReadOnly

ReadOnly.propTypes = {
  edge: PropTypes.string,
  hysteresis: PropTypes.number,
  withBitTable: PropTypes.bool,
  numberOfBits: PropTypes.number,
  startBit: PropTypes.number,
  inputConfig: PropTypes.string,
  operator: PropTypes.number,
  carryType: PropTypes.string,
  selectedInputDescription: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueMin: PropTypes.number,
  valueMax: PropTypes.number,
  ignoreCarry: PropTypes.bool,
  isSixteenBitCanOutput: PropTypes.bool,
  inputUri: PropTypes.string,
  subType: PropTypes.string,
  pinLocation: PropTypes.string,
  result: PropTypes.bool,
  units: PropTypes.string,
  isSingleCondition: PropTypes.bool,
  translations: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      translation: PropTypes.string,
    })
  ),
  translationType: PropTypes.string,
  switchNumber: PropTypes.string,
  isScalingQuery: PropTypes.bool,
  featureModuleName: PropTypes.string,
  floatingCircuit: PropTypes.string,
  featureType: PropTypes.string,
  switchDescription: PropTypes.string,
  currentSignalSwitches: PropTypes.object,
  canId: PropTypes.string,
  interfacePins: PropTypes.array,
  customDescription: PropTypes.string,
}
